import _flowRight from "lodash/flowRight";
import moment from 'moment-timezone';
import { rotate } from './utils';
/**
 * Timezone helpers for CTM date/time strings.
 *
 * Common shapes (often no `Z` / offset on the string):
 * - UTC: visit `effectivedatetime`, `formatPimDate` output — digits are UTC.
 *   Use `utcToSiteMoment` / `utcToTz`.
 * - Site-local: `*_shifted`, HOPE `visitEligibleFrom` — digits are agency/site local.
 *   Use `siteLocalToMoment`.
 * - Instant: ISO with `Z` — real UTC instant; `utcToSiteMoment` still applies.
 *
 * Do not compare a UTC string to a site-local string with plain `moment()` —
 * calendar days can disagree near midnight (e.g. evening Eastern vs next UTC date).
 */

export const momentToDate = time => new Date(time.format().substring(0, time.format().lastIndexOf('-')));
export function dateToMoment(dateTime) {
  if (!dateTime) return moment();
  const mo1 = moment(dateTime); // in browser default time zone, e.g. America/Denver

  const mo2 = moment(dateTime).tz(moment.tz.guess()); // in current state time zone e.g. Africa/Bamako

  const modifiedDate = moment(dateTime);
  modifiedDate.subtract(mo1.utcOffset() - mo2.utcOffset(), 'minutes');
  return modifiedDate;
}

const dateFrom = (year, month, day) => new Date(year, month - 1, // JavaScript counts months from 0
day);

const spliceFirstT = a => a.split('T')[0];

const spliceFirstSpace = a => a.split(' ')[0]; // the idea being that any date format will use either space OR T as the separator


const spliceDateSegment = _flowRight(spliceFirstT, spliceFirstSpace);

const splitHyphens = a => a.split('-');

const splitSlashes = a => a.split('/');

const extractHyphenDateParts = _flowRight(splitHyphens, spliceDateSegment);

const extractSlashDateParts = _flowRight(splitSlashes, spliceDateSegment); // YYYY-MM-DDT00.... OR YYYY-MM-DD 00......


export const parseDateYYYYMMDD = d => {
  const parts = extractHyphenDateParts(d).map(Number);
  return dateFrom(...parts);
}; // MM/DD/YYYY hh:mm

export const parseDateMMDDYYYYSlashes = d => {
  const parts = extractSlashDateParts(d).map(Number); // todo rotateRight function

  const YMD = [parts[2], parts[0], parts[1]];
  return dateFrom(...YMD);
};

const appendLeadingZero = n => `0${n}`;

const formatDateNumber = n => n.length === 1 ? appendLeadingZero(n) : n;

const splitDateValues = d => [d.getFullYear(), d.getMonth() + 1, d.getDate()];

const splitDateValuesNoCentury = d => [d.getFullYear() % 100, d.getMonth() + 1, d.getDate()];

const formatDateValues = d => d.map(String).map(formatDateNumber);

const getDateComponents = _flowRight(formatDateValues, splitDateValues);

const getDateComponentsNoCentury = _flowRight(formatDateValues, splitDateValuesNoCentury);

export const dateToYYYYMMDD = d => getDateComponents(d).join('-');
export const dateToYYMMDD = d => getDateComponentsNoCentury(d).join('-');
export const dateToMMDDYYYY = d => rotate(getDateComponents(d), 1).join('/');
export const dateToMMDDYYYYHyphens = d => rotate(getDateComponents(d), 1).join('-');
export const dateToMMDDYY = d => rotate(getDateComponentsNoCentury(d), 1).join('/');
export const addMomentMins = duration => start => start.add(duration, 'minute');
export const dateAsMoment = func => _flowRight(momentToDate, func, dateToMoment);
/** Parse a UTC date/time string (or Date) into a Moment in the site timezone. */

export const utcToSiteMoment = (d, siteTimezone) => moment.utc(d).tz(siteTimezone);
/** Parse a site-local date/time string (no zone) as wall time in the site timezone. */

export const siteLocalToMoment = (d, siteTimezone) => moment.tz(d, siteTimezone);
export const utcToTz = (d, timezone) => momentToDate(utcToSiteMoment(d, timezone));